import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import get from 'lodash/get';

import { Modal } from 'sa-ui';
import { api } from '../../app/api';

import { getTermsContent } from './SimulatedCorrectionTermsOfUseModal';
import SimulatedCorrectionManualStudentSelectionModal from './SimulatedCorrectionManualStudentSelectionModal';
import messages from './SimulatedCorrectionUploadAnswerCardModalMessages';
import termsMessages from './simulatedCorrectionTermsOfUseModalMessages';

import './SimulatedCorrectionUploadAnswerCardModal.scss';

const STEP_TERMS = 1;
const STEP_UPLOAD = 2;

/*
 const formData = new FormData();
          formData.append('idEvaluation', props.idEvaluation);
          formData.append('idCSchool', filter.idCSchool);
          formData.append('idCourse', filter.idESCourse);
          formData.append('file', file);
          props.correctAnswerSheet(formData);
*/

const Status = {
  PENDING: 0,
  UPLOADING: 1,
  UPLOADED: 2,
  ERROR: 3,
};

const SimulatedCorrectionUploadAnswerCardModal = (props) => {
  const inputFileRef = useRef();
  const manualResolveRef = useRef(null);
  const overwriteConfirmResolveRef = useRef(null);
  const [step, setStep] = useState(STEP_TERMS);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [manualSelection, setManualSelection] = useState(null);
  const [overwriteConfirm, setOverwriteConfirm] = useState(null);

  function getUploadErrorMessage(error) {
    switch (true) {
      case /There is already a correction for this student/g.test(error):
        return messages.errorAlreadyExists;
      case /Student not found/g.test(error):
        return messages.errorStudentNotFound;
      case /This user has no permission for this idCSchool/g.test(error):
        return messages.errorNoPermission;
      case /AnswerSheet dont match with evaluation/g.test(error):
        return messages.errorAnswersheetNotMatchEvaluation;
      default:
        return messages.errorGeneric;
    }
  }

  function uploadFile(
    file,
    idStudent = null,
    scannedResults = null,
    confirmOverwrite = false,
    type1Scan = false,
  ) {
    const formData = new FormData();
    formData.append('idEvaluation', props.idEvaluation);
    formData.append('idCSchool', props.idCSchool);
    formData.append('idCourse', props.idESCourse);
    formData.append('file', file);
    formData.append('optional-qrcode', props.allowManualSelection ? '1' : '0');
    if (idStudent) formData.append('idStudent', idStudent);
    if (scannedResults != null) formData.append('scannedResults', JSON.stringify(scannedResults));
    if (confirmOverwrite) formData.append('confirmOverwrite', '1');
    if (type1Scan) formData.append('type1Scan', '1');
    props.onError(false);
    props.onErrCode(false);
    return api
      .post('/evaluation-system/correctAnswerSheet.php', formData)
      .then(() => Promise.resolve(null))
      .catch((err) => {
        const status = get(err, 'response.status');
        const serverErrorMessage = get(err, 'response.data.error');
        const serverErrCode = get(err, 'response.data.errCode');
        const serverScannedResults = get(err, 'response.data.scannedResults');
        const serverManyResults = get(err, 'response.data.manyResults');
        const serverType1Scan = get(err, 'response.data.type1Scan');
        if (
          (status === 400 || status === 403) &&
          /select student manually/i.test(String(serverErrorMessage))
        ) {
          return Promise.resolve({
            manualSelection: true,
            scannedResults: serverScannedResults,
            manyResults: Boolean(serverManyResults),
            type1Scan: Boolean(serverType1Scan),
          });
        }
        if (
          (status === 400 || status === 403) &&
          /confirm overwrite/i.test(String(serverErrorMessage))
        ) {
          return Promise.resolve({ confirmOverwrite: true, fullName: get(err, 'response.data.fullName') || '' });
        }
        console.error('erro no upload', err.response && err.response.data);
        const messageError = getUploadErrorMessage(serverErrorMessage);
        const messageIntl = props.intl.formatMessage(messageError);
        props.onError(messageIntl);
        props.onErrCode(serverErrCode);
        return Promise.resolve(messageIntl);
      });
  }

  function enqueueFiles(newFiles) {
    const pendingFiles = newFiles.map((file, index) => ({
      file,
      id: index + files.length,
      status: Status.PENDING,
    }));
    const previousFiles = files.some((f) => f.status === Status.ERROR) ? [] : files;
    setFiles(previousFiles.concat(pendingFiles));
    console.log('uploading files...', pendingFiles, previousFiles.concat(pendingFiles));
  }

  function updateFileStatus(fileContainerId, success) {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileContainerId
          ? {
              ...f,
              status: success ? Status.UPLOADED : Status.ERROR,
              message: success
                ? props.intl.formatMessage(messages.uploadedSuccess)
                : props.intl.formatMessage(messages.errorGeneric),
            }
          : f,
      ),
    );
    if (manualResolveRef.current) {
      manualResolveRef.current();
      manualResolveRef.current = null;
    }
    setManualSelection(null);
  }

  async function handleReuploadWithStudent(
    file,
    idStudent,
    scannedResults,
    confirmOverwrite = false,
    type1Scan = false,
  ) {
    const result = await uploadFile(file, idStudent, scannedResults, confirmOverwrite, type1Scan);
    if (result == null) return { success: true };
    if (result && result.manualSelection) return { success: false };
    if (result && result.confirmOverwrite) return { confirmOverwrite: true };
    return { success: false, message: result };
  }

  async function sendFiles() {
    const pendingFiles = files.filter((c) => c.status === Status.PENDING);
    setFiles((prev) =>
      prev.map((f) =>
        f.status === Status.PENDING
          ? {
              ...f,
              status: Status.UPLOADING,
              message: props.intl.formatMessage(messages.uploading),
            }
          : f,
      ),
    );
    setLoading(true);
    try {
      for (const fileContainer of pendingFiles) {
        const result = await uploadFile(fileContainer.file);
        if (result && result.manualSelection) {
          setManualSelection({
            file: fileContainer.file,
            fileContainerId: fileContainer.id,
            scannedResults: result.scannedResults,
            manyResults: result.manyResults,
            type1Scan: Boolean(result.type1Scan),
          });
          await new Promise((resolve) => {
            manualResolveRef.current = resolve;
          });
          continue;
        }
        if (result && result.confirmOverwrite) {
          setOverwriteConfirm({ file: fileContainer.file, fileContainerId: fileContainer.id, fullName: result.fullName });
          const confirmed = await new Promise((resolve) => {
            overwriteConfirmResolveRef.current = resolve;
          });
          const retryResult = confirmed
            ? await uploadFile(fileContainer.file, null, null, true)
            : props.intl.formatMessage(messages.errorGeneric);
          const success = !retryResult;
          const message = success
            ? props.intl.formatMessage(messages.uploadedSuccess)
            : typeof retryResult === 'string'
              ? retryResult
              : props.intl.formatMessage(messages.errorGeneric);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileContainer.id
                ? { ...f, status: success ? Status.UPLOADED : Status.ERROR, message }
                : f,
            ),
          );
          continue;
        }
        const success = !result;
        const message = result || props.intl.formatMessage(messages.uploadedSuccess);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileContainer.id
              ? { ...f, status: success ? Status.UPLOADED : Status.ERROR, message }
              : f,
          ),
        );
      }
      if (props.onCompleteUpload) props.onCompleteUpload(pendingFiles);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (props.show) {
      setStep(props.initialStep === 2 ? STEP_UPLOAD : STEP_TERMS);
      setFiles([]);
      setLoading(false);
    }
  }, [props.show, props.initialStep]);

  const onDrop = useCallback((acceptedFiles) => {
    enqueueFiles(acceptedFiles);
  }, []);

  function handleUpload(e) {
    const acceptedFiles = Array.from(e.target.files);
    enqueueFiles(acceptedFiles);
  }

  function handleTriggerUpload() {
    if (loading) return;
    inputFileRef.current.click();
  }

  const { getRootProps } = useDropzone({ onDrop, accept: 'application/pdf,application/zip,image/jpeg,image/png' });

  const allUploaded = files.length > 0 && files.every((f) => f.status === Status.UPLOADED);

  const step1Buttons = [
    { label: props.intl.formatMessage(termsMessages.back), onClick: props.onClose },
    {
      label: props.intl.formatMessage(termsMessages.agreeAndContinue),
      onClick: () => setStep(STEP_UPLOAD),
      primary: true,
    },
  ];

  const step2Buttons = [
    {
      label: props.intl.formatMessage(termsMessages.back),
      onClick: () => {
        if (allUploaded) {
          props.onClose();
        } else {
          setStep(STEP_TERMS);
        }
      },
    },
    {
      label: loading
        ? props.intl.formatMessage(messages.loading)
        : allUploaded
          ? props.intl.formatMessage(messages.finish)
          : props.intl.formatMessage(messages.continue),
      onClick: allUploaded ? props.onClose : sendFiles,
      primary: true,
      disabled: loading || files.some((f) => f.status === Status.UPLOADING),
    },
  ];

  return (
    <>
      <Modal
        title={
          step === STEP_TERMS
            ? props.intl.formatMessage(termsMessages.title)
            : props.singularFile
              ? props.intl.formatMessage(messages.singularTitle)
              : props.intl.formatMessage(messages.pluralTitle)
        }
        show={props.show}
        onClose={props.onClose}
        buttons={step === STEP_TERMS ? step1Buttons : step2Buttons}
      >
        {step === STEP_TERMS ? (
          <div className="text-left" dangerouslySetInnerHTML={{ __html: getTermsContent(props.intl) }} />
        ) : (
          <div className="upload-answer-modal">
            <div {...getRootProps()} onClick={handleTriggerUpload} className="upload-border">
              <br />
              <i className="icon icon-small ti-cloud-up" />
              <h6 className="subtitle subtitle-bigger mt-2">
                {props.intl.formatMessage(messages.dragDrop)}
                <br />
                <span>
                  <input
                    ref={inputFileRef}
                    type="file"
                    id="file"
                    name="file"
                    accept=".pdf, .zip, image/jpeg, image/png"
                    style={{ display: 'none' }}
                    onChange={handleUpload}
                    disabled={loading}
                    multiple={!props.singularFile}
                  />

                  {props.intl.formatMessage(messages.clickHere)}
                </span>
              </h6>
            </div>
            {!props.singularFile && (
              <p style={{ color: '#969696' }} className="mb-0">
                {props.intl.formatMessage(messages.alreadyAdded, { files: files.length })}
              </p>
            )}
            <div className="table-container">
              <table>
                <tbody>
                  {files.map((fileContainer) => (
                    <tr
                      className={classNames('text-left', {
                        'text-success': fileContainer.status === Status.UPLOADED,
                        'text-danger': fileContainer.status === Status.ERROR,
                      })}
                    >
                      <td>
                        <i className="far fa-file-alt" />
                        {fileContainer.file.name}
                      </td>
                      <td>{fileContainer.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
      <SimulatedCorrectionManualStudentSelectionModal
        show={!!manualSelection}
        students={props.students || []}
        file={manualSelection && manualSelection.file}
        scannedResults={manualSelection && manualSelection.scannedResults}
        manyResults={!!(manualSelection && manualSelection.manyResults)}
        onReupload={(file, idStudent, scannedResults, confirmOverwrite) =>
          handleReuploadWithStudent(
            file,
            idStudent,
            scannedResults,
            confirmOverwrite,
            !!(manualSelection && manualSelection.type1Scan),
          )
        }
        onSuccess={() =>
          manualSelection && updateFileStatus(manualSelection.fileContainerId, true)
        }
        onError={() =>
          manualSelection && updateFileStatus(manualSelection.fileContainerId, false)
        }
        onClose={() =>
          manualSelection && updateFileStatus(manualSelection.fileContainerId, false)
        }
      />
      <Modal
        show={!!overwriteConfirm}
        title={props.intl.formatMessage(messages.overwriteTitle)}
        onClose={() => {
          if (overwriteConfirmResolveRef.current) {
            overwriteConfirmResolveRef.current(false);
            overwriteConfirmResolveRef.current = null;
          }
          setOverwriteConfirm(null);
        }}
        buttons={[
          {
            label: props.intl.formatMessage(messages.cancel),
            onClick: () => {
              if (overwriteConfirmResolveRef.current) {
                overwriteConfirmResolveRef.current(false);
                overwriteConfirmResolveRef.current = null;
              }
              setOverwriteConfirm(null);
            },
          },
          {
            label: props.intl.formatMessage(messages.overwriteConfirm),
            onClick: () => {
              if (overwriteConfirmResolveRef.current) {
                overwriteConfirmResolveRef.current(true);
                overwriteConfirmResolveRef.current = null;
              }
              setOverwriteConfirm(null);
            },
            primary: true,
          },
        ]}
      >
        <p>{props.intl.formatMessage(messages.overwriteMessage, { studentName: (overwriteConfirm && overwriteConfirm.fullName) || '' })}</p>
      </Modal>
    </>
  );
};

export default injectIntl(SimulatedCorrectionUploadAnswerCardModal);
